import React from 'react';
import { token } from '../../ui/theme';

// ─── Row types ────────────────────────────────────────────────────────────────

export interface PointCell {
  won: boolean;
  /** Pass undefined when neither team can win this point (e.g. jack not in play, tied game) */
  noWinner?: boolean;
  label?: string;
}

export interface ValueCell {
  primary: string | number;
  secondary?: string;
  color?: string;
}

export type ScoreTableRow =
  | { type: 'point'; label: string; cells: PointCell[] }
  | { type: 'value'; label: string; cells: ValueCell[] }
  | { type: 'divider' };

// ─── Sub-components ───────────────────────────────────────────────────────────

const labelCellStyle: React.CSSProperties = {
  border: 'none',
  color: token('color-muted'),
  fontSize: token('font-size-xs'),
  padding: 'var(--space-2) var(--space-4) var(--space-2) 0',
  whiteSpace: 'nowrap',
  textAlign: 'left',
};

const baseCellStyle: React.CSSProperties = {
  border: 'none',
  padding: 'var(--space-2) var(--space-4)',
  textAlign: 'center',
};

function PointCellEl({ cell }: { cell: PointCell }) {
  if (cell.noWinner) {
    return (
      <td
        style={{ ...baseCellStyle, color: token('color-muted'), fontSize: token('font-size-sm') }}
      >
        —
      </td>
    );
  }
  return (
    <td style={baseCellStyle}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          color: cell.won ? token('color-win') : token('color-lose'),
          fontWeight: 600,
          fontSize: token('font-size-sm'),
        }}
      >
        {cell.won ? '✓' : '✗'}
        {cell.label && (
          <span
            style={{
              color: cell.won ? token('color-win') : token('color-muted'),
              fontWeight: cell.won ? 600 : 400,
            }}
          >
            {cell.label}
          </span>
        )}
      </span>
    </td>
  );
}

function ValueCellEl({ cell }: { cell: ValueCell }) {
  return (
    <td style={baseCellStyle}>
      <span
        style={{
          color: cell.color ?? token('color-text'),
          fontWeight: 700,
          fontSize: token('font-size-md'),
        }}
      >
        {cell.primary}
      </span>
      {cell.secondary && (
        <span style={{ color: token('color-muted'), fontSize: token('font-size-xs') }}>
          {cell.secondary}
        </span>
      )}
    </td>
  );
}

// ─── ScoreTable ───────────────────────────────────────────────────────────────

interface ScoreTableProps {
  columns: string[];
  rows: ScoreTableRow[];
}

export function ScoreTable({ columns, rows }: ScoreTableProps) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: token('font-sans') }}>
      <thead>
        <tr>
          <th style={{ ...labelCellStyle, paddingBottom: 'var(--space-1)' }} />
          {columns.map((col) => (
            <th
              key={col}
              style={{
                border: 'none',
                color: token('color-accent'),
                fontWeight: 700,
                fontSize: token('font-size-sm'),
                paddingBottom: 'var(--space-1)',
                letterSpacing: token('letter-spacing-wide'),
                textAlign: 'center',
              }}
            >
              {col}
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {rows.map((row, i) => {
          if (row.type === 'divider') {
            return (
              <tr key={i}>
                <td colSpan={columns.length + 1} style={{ border: 'none', padding: '6px 0' }}>
                  <div style={{ height: 1, background: token('color-border') }} />
                </td>
              </tr>
            );
          }

          return (
            <tr key={i}>
              <td style={labelCellStyle}>{row.label}</td>
              {row.type === 'point'
                ? row.cells.map((cell, j) => <PointCellEl key={j} cell={cell} />)
                : row.cells.map((cell, j) => <ValueCellEl key={j} cell={cell} />)}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
